/**
 * Chart Safety Utilities
 *
 * Provides safety checks and fallbacks for chart components
 */

import type { Trace } from '@/features/traceability/type/Tool';
import type { AggregationBy } from '../type/dynamicChart';

/**
 * Check if reference aggregation is available in the traces
 */
function isReferenceAvailable(traces: (Trace & { reference?: string })[]): boolean {
  return traces.some((trace) => trace.reference != null && trace.reference !== '');
}

/**
 * Get safe aggregation level with fallback
 * If 'reference' aggregation is requested but not available, fallback to 'tool'
 */
export function getSafeAggregationLevel(
  requestedLevel: AggregationBy | 'category' | undefined,
  traces: (Trace & { reference?: string })[],
  isCategorical: boolean
): AggregationBy | 'category' {
  // Categorical always uses 'category'
  if (isCategorical) {
    return 'category';
  }

  // Default to 'tool' if no level specified
  if (!requestedLevel || requestedLevel === 'category') {
    return 'tool';
  }

  // If reference is requested but not available, fallback to tool
  if (requestedLevel === 'reference' && !isReferenceAvailable(traces)) {
    if (import.meta.env.DEV) {
      console.warn(
        'Reference aggregation requested but no reference data available. Falling back to tool aggregation.'
      );
    }
    return 'tool';
  }

  return requestedLevel;
}
